import FormType from '../constants/form-type';
import PasswordMode from '../constants/password-mode';
import ParamStore from './stores/param-store';
import EmailRepository from './email-repository';
import FormActions from '../logic/form-actions';

import {localize} from '../infrastructure/localization';

class Validator {
    constructor() {
        this.enabled = false;
    }

    get emailRegexp() {
        return /^((?!(.+\.\.+))(?!(.+\.@+))(?!(^\.))([\w-+!#\$\.']+))@((?:[\w-]+\.)*\w[\w-]*)\.([a-z]{2,})$/i;
    }

    _validateEmailAsync(componentId, model, force) {
        var errors = model.validation.errors;

        switch (model.formType) {
            case FormType.login:
            case FormType.register: {

                var forceByErrorType = false;
                var error = !model.email ? localize('validation.email.required') : '';

                if (!error && model.email.length > 70) {
                    error = localize('validation.email.max-length');
                    forceByErrorType = true;
                }

                error = error || (!this.emailRegexp.test(model.email) ? localize('validation.email.format') : '');

                if (!error) {
                    model.validation.hasEmailBeenValid = true;
                }

                if (!force && !forceByErrorType && !model.validation.hasEmailBeenEdited
                    && !model.validation.hasEmailBeenValid) {
                    errors.email = '';
                    return new Promise((resolve) => resolve());
                }

                if (error) {
                    errors.email = error;
                    return new Promise((resolve) => resolve());
                }

                let existCheck = EmailRepository.isEmailExisting(componentId, model.email);
                let federatedCheck = EmailRepository.isEmailFederated(componentId, model.email);

                return Promise.all([existCheck, federatedCheck])
                    .catch(ex => new Promise((resolve) => resolve({error: true})))
                    .then((checkResults) => {
                        if (checkResults.error) {
                            errors.email = localize('error.generic');
                            return checkResults;
                        }

                        const isEmailExisting = checkResults[0].exists;
                        const hasPassword = checkResults[0].hasPassword;
                        const isEmailFederated = checkResults[1];

                        if (!isEmailFederated && !isEmailExisting) {
                            errors.email = '';
                            return checkResults;
                        }

                        // Federated email warning has higher priority than existing email warning
                        if (isEmailFederated) {
                            const clsLoginUrl = `${ParamStore.getModel(componentId).clsLoginUrl}`
                                + `&email=${encodeURIComponent(model.email)}`;
                            errors.email =
                                /* eslint-disable quotes */
                                localize('validation.email.federated-id')
                                    .replace('{0}', `<a href="javascript: void(0)" onclick="event.stopPropagation();`
                                        + `jwfLogin._Api.Links.federatedLoginLinkClicked(\'${clsLoginUrl}\',${componentId})" `
                                        + `data-gaeventcategory="`
                                        + ParamStore.getModel(componentId).tracking.ga.federatedLoginLink.category
                                        + `" data-gaeventaction="`
                                        + ParamStore.getModel(componentId).tracking.ga.federatedLoginLink.action + `">`)
                                    .replace('{1}', '</a>');
                            /* eslint-enable quotes */
                        } else if (isEmailExisting) {

                            if (model.formType === FormType.login) {

                                if (hasPassword) {
                                    errors.email = '';
                                } else {
                                    // PWD less flow
                                    FormActions.redirectToForgotPwd(componentId);
                                }

                            } else {
                                /* eslint-disable quotes */
                                errors.email = ParamStore.getModel(componentId).disableRedirectToLogin
                                    ? localize('validation.email.existing-id-short')
                                    : localize('validation.email.existing-id')
                                        .replace('{0}', `<a href='javascript: void(0)' `
                                            + `onclick='event.stopPropagation();`
                                            + `jwfLogin._Api.Links.existingLoginLinkClicked(${hasPassword}, ${componentId})' `
                                            + `data-gaeventcategory='`
                                            + ParamStore.getModel(componentId).tracking.ga.existingLoginLink.category
                                            + `' data-gaeventaction='`
                                            + ParamStore.getModel(componentId).tracking.ga.existingLoginLink.action
                                            + `'>`)
                                        .replace('{1}', '</a>');

                                /* eslint-enable quotes */
                            }

                        } else {
                            errors.email = '';
                        }

                        return checkResults;
                    });
            }

            case FormType.forgotPassword: {
                return new Promise((resolve) => resolve());
            }

            default: {
                return new Promise((resolve) => resolve());
            }
        }
    }

    validateEmailAsync(componentId, model) {
        return this.executeIfEnabledAsync(() =>
            this._validateEmailAsync(componentId, model, false)
                .then(() => this._updateHasErrors(model)));
    }

    /* eslint-disable complexity */
    _validatePassword(model, force) {
        var errors = model.validation.errors;

        switch (model.formType) {
            case FormType.login:
            case FormType.register:
                if (model.passwordMode === PasswordMode.passwordless) {
                    break;
                }

                var error = !model.password ? localize('validation.password.required') : '';
                error = error || (model.password.length < 6 ? localize('validation.password.min-length') : '');
                error = error || (model.password.length > 255 ? localize('validation.password.max-length') : '');

                if (!error) {
                    model.validation.hasPasswordBeenValid = true;
                }

                this._validateConfirmedPassword(model, false);

                if (!force && !model.validation.hasPasswordBeenEdited && !model.validation.hasPasswordBeenValid) {
                    break;
                }

                errors.password = error;

                break;

            case FormType.forgotPassword:
                break;

            default:
                return;
        }
    }
    /* eslint-enable complexity */

    validatePassword(model) {
        return this.executeIfEnabled(() => {
            this._validatePassword(model, false);
            this._updateHasErrors(model);
        });
    }

    _validateConfirmedPassword(model, force) {
        var errors = model.validation.errors;

        switch (model.formType) {
            case FormType.login:
            case FormType.forgotPassword:
                break;

            case FormType.register:
                if (model.passwordMode !== PasswordMode.standard) {
                    break;
                }

                var error = model.confirmedPassword !== model.password
                    ? localize('validation.confirm-password.mismatch')
                    : '';

                if (!error) {
                    model.validation.hasConfirmedPasswordBeenValid = true;
                }

                if (!force && !model.validation.hasConfirmedPasswordBeenEdited
                    && !model.validation.hasConfirmedPasswordBeenValid) {
                    break;
                }

                errors.confirmedPassword = error;

                break;

            default:
                return;
        }
    }

    validateConfirmedPassword(model) {
        return this.executeIfEnabled(() => {
            this._validateConfirmedPassword(model, false);
            this._updateHasErrors(model);
        });
    }

    _validateAcceptTerms(model) {
        var errors = model.validation.errors;

        switch (model.formType) {
            case FormType.login:
            case FormType.forgotPassword:
                break;

            case FormType.register:
                errors.acceptTerms = !model.acceptTerms
                    ? localize('validation.accept-terms')
                    : '';
                break;

            default:
                return;
        }
    }

    validateAcceptTerms(model) {
        return this.executeIfEnabled(() => {
            this._validateAcceptTerms(model);
            this._updateHasErrors(model);
        });
    }

    validateAllAsync(componentId, model) {
        return this.executeIfEnabledAsync(() =>
            this._validateEmailAsync(componentId, model, true).then(() => {
                this._validatePassword(model, true);
                this._validateConfirmedPassword(model, true);
                this._validateAcceptTerms(model);
                this._updateHasErrors(model);
            }));
    }

    _updateHasErrors(model) {
        model.validation.hasErrors =
            !!model.validation.errors.email
            || !!model.validation.errors.password
            || !!model.validation.errors.confirmedPassword
            || !!model.validation.errors.acceptTerms;
    }

    clearSubmitError(model) {
        model.validation.errors.submitError = '';
    }

    set(model, validationModel) {
        model.validation = validationModel;
    }

    enable() {
        this.enabled = true;
    }

    executeIfEnabled(func) {
        if (!this.enabled) {
            return new Promise((resolve, reject) => reject());
        }

        return func();
    }

    executeIfEnabledAsync(func) {
        if (!this.enabled) {
            return new Promise((resolve) => resolve());
        }

        return func();
    }
}

export default new Validator();



/** WEBPACK FOOTER **
 ** ./src/logic/validator.js
 **/